import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { FlaskConical, Copy, Rocket } from 'lucide-react';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { xurveyApi, xurveyPublicUrl, type XurveyDeployment } from '@/lib/api/xurvey';

/**
 * El QR de PRUEBA del Design Journey (F2b).
 *
 * Un QR imprimible que se escanea en un móvil de verdad pero no puede hacer
 * de evento: 10 jugadas, 24 horas, premios falsos y nada se guarda. Vive
 * junto al Guardar porque es parte del mismo gesto — diseñar, probar en la
 * mano, y sólo entonces salir en vivo. «Activar» lo vuelve un despliegue
 * normal (gratis dentro del cupo de eventos; al tope, el camino es el
 * event-pack de $100 desde Facturación).
 */
export function TestQrDialog({
  surveyId,
  entityId,
  testLink,
  onCreated,
}: {
  surveyId: number;
  /** La estación de referencia contra la que se acuña el QR. */
  entityId: string | null;
  /** El QR de prueba vivo de esta encuesta, si ya hay uno. */
  testLink: XurveyDeployment | null;
  /**
   * Justo después de crear el enlace de prueba, ANTES de anunciarlo.
   *
   * El Design Journey lo usa para volcarle el borrador actual: un QR de
   * prueba nace sin overrides y, sin esto, enseñaba la encuesta tal como
   * viene de plantilla, sin el fondo ni los colores que se estaban
   * probando — que es justo lo que se quería ver en el móvil.
   */
  onCreated?: (link: XurveyDeployment) => Promise<unknown> | void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const crear = useMutation({
    mutationFn: async () => {
      const link = await xurveyApi.createDeployment(surveyId, { entityId: entityId ?? '', isTest: true });
      await onCreated?.(link);
      return link;
    },
    onSuccess: () => {
      // La MISMA clave literal que usa DesignJourneySection para su lista.
      void qc.invalidateQueries({ queryKey: ['xurvey', 'deployments', surveyId] });
      toast({ tone: 'success', title: t('dj.testQr.created') });
    },
    onError: (e) =>
      toast({ tone: 'error', title: t('dj.testQr.failed'), description: (e as Error).message }),
  });

  const activar = useMutation({
    mutationFn: (id: number) => xurveyApi.activateDeployment(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['xurvey', 'deployments', surveyId] });
      toast({ tone: 'success', title: t('dj.testQr.activated'), description: t('dj.testQr.activatedBody') });
      setOpen(false);
    },
    onError: (e) =>
      toast({ tone: 'error', title: t('dj.testQr.failed'), description: (e as Error).message }),
  });

  const url = useMemo(
    () => (testLink ? xurveyPublicUrl(testLink.slug) : null),
    [testLink],
  );
  const caduca = testLink?.testExpiresAt ? dayjs(testLink.testExpiresAt) : null;
  const caducado = caduca ? caduca.isBefore(dayjs()) : false;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        leftIcon={<FlaskConical className="h-4 w-4" />}
        onClick={() => setOpen(true)}
      >
        {t('dj.testQr.button')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dj.testQr.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
          <p className="text-sm leading-relaxed text-[var(--color-fg-muted)]">
            {t('dj.testQr.body')}
          </p>

          {testLink && url && !caducado ? (
            <>
              <div className="flex items-start gap-4">
                <div className="rounded-xl border border-[var(--color-border-default)] bg-white p-3">
                  <QRCodeSVG value={url} size={132} level="M" />
                </div>
                <div className="min-w-0 flex-1 space-y-2 text-sm">
                  <p className="break-all font-mono text-xs text-[var(--color-fg-muted)]">{url}</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    leftIcon={<Copy className="h-3.5 w-3.5" />}
                    onClick={() => {
                      void navigator.clipboard.writeText(url);
                      toast({ tone: 'info', title: t('dj.testQr.copied') });
                    }}
                  >
                    {t('dj.testQr.copy')}
                  </Button>
                  <p className="text-xs text-[var(--color-fg-muted)]">
                    {t('dj.testQr.limits')}
                    {caduca ? ` · ${t('dj.testQr.expires', { when: caduca.format('MMM D, HH:mm') })}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border-default)] pt-4">
                <p className="text-xs text-[var(--color-fg-muted)]">{t('dj.testQr.activateHint')}</p>
                <Button
                  type="button"
                  size="sm"
                  loading={activar.isPending}
                  leftIcon={<Rocket className="h-4 w-4" />}
                  onClick={() => activar.mutate(testLink.id)}
                >
                  {t('dj.testQr.activate')}
                </Button>
              </div>
            </>
          ) : (
            <Button
              type="button"
              className="w-full"
              loading={crear.isPending}
              disabled={!entityId}
              leftIcon={<FlaskConical className="h-4 w-4" />}
              onClick={() => crear.mutate()}
            >
              {caducado ? t('dj.testQr.createAgain') : t('dj.testQr.create')}
            </Button>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
